import React, { useEffect, useState } from 'react';
import Modal from 'components/UI/Modal';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Background, Main, Shade } from 'styles/themeColors';
import styled from 'styled-components';
import { TEXT_LABELS } from 'constants/productDetails';
import { SAMPLE_PROMPTS } from 'constants/samplePrompts';
import UserGuide from './UserGuide';

type ModalTab = typeof TEXT_LABELS.SAMPLE_PROMPTS | typeof TEXT_LABELS.USER_GUIDE;

const TopTabBar = styled.div`
  display: flex;
  border-bottom: 2px solid ${Shade.shade120};
  margin-bottom: 20px;
`;

const TopTab = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? Main.brandDark : 'transparent')};
  margin-bottom: -2px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  color: ${({ active }) => (active ? Main.brandDark : Main.gray)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${Main.brandDark};
  }
`;

const SamplePromptItem = styled.div`
  padding: 12px 16px;
  margin: 8px 0;
  background-color: ${Background.white};
  border: 1px solid ${Background.shaded};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${Background.shaded};
    border-color: ${Main.brandDark};
  }
`;

const SamplePromptTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${Main.black};
`;

const SamplePromptDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${Main.gray};
`;

const ScrollableContent = styled.div`
  max-height: 480px;
  overflow-y: auto;
  padding-right: 6px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${Shade.shade230};
    border-radius: 4px;
  }
`;

interface Props {
  showModal: boolean;
  onClose: () => void;
  onSelectPrompt: (prompt: string) => void;
}

export default function SamplePromptsModal({
  showModal,
  onClose,
  onSelectPrompt,
}: Props) {

const [activeTab, setActiveTab] = useState<ModalTab>(TEXT_LABELS.SAMPLE_PROMPTS);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showModal]);

  // Reset to sample prompts tab when modal is closed and reopened
  useEffect(() => {
    if (!showModal) {
      setActiveTab(TEXT_LABELS.SAMPLE_PROMPTS);
    }
  }, [showModal]);

  const handlePromptClick = (prompt: string) => {
    onSelectPrompt(prompt);
    onClose();
  };

  return (
    <Modal
      showModal={showModal}
      showCloseIcon
      width={'850px'}
      onClose={onClose}
      onClick={onClose}
      primaryButtonText={TEXT_LABELS.CONFIRM}
      ghostButtonText={TEXT_LABELS.CANCEL}
      title={activeTab === TEXT_LABELS.SAMPLE_PROMPTS ? 'Sample Prompts' : 'User Guide'}
      modalFooterMargin="0"
      left="30%"
      showButtons={false}
      theme="accent"
    >
      <TopTabBar>
        <TopTab
          active={activeTab === TEXT_LABELS.SAMPLE_PROMPTS}
          onClick={() => setActiveTab(TEXT_LABELS.SAMPLE_PROMPTS)}
        >
          Sample Prompts
        </TopTab>
        <TopTab
          active={activeTab === TEXT_LABELS.USER_GUIDE}
          onClick={() => setActiveTab(TEXT_LABELS.USER_GUIDE)}
        >
          Guide
        </TopTab>
      </TopTabBar>

      {activeTab === TEXT_LABELS.SAMPLE_PROMPTS && (
        <ScrollableContent>
          <FlexboxContainer flexDirection="column" gap="0">
            {SAMPLE_PROMPTS.map((sample, index) => (
              <SamplePromptItem
                key={index}
                onClick={() => handlePromptClick(sample.prompt)}
              >
                <SamplePromptTitle>{sample.title}</SamplePromptTitle>
                <SamplePromptDescription>
                  {sample.description}
                </SamplePromptDescription>
                <div
                  style={{
                    marginTop: '8px',
                    fontSize: '13px',
                    color: Main.brandDark,
                    fontStyle: 'italic',
                  }}
                >
                  "{sample.prompt}"
                </div>
              </SamplePromptItem>
            ))}
          </FlexboxContainer>
        </ScrollableContent>
      )}
      
      {activeTab === TEXT_LABELS.USER_GUIDE && <UserGuide />}
    </Modal>
  );
}
